/**
 * 绑定知识提取器：XML → IR → 绑定知识（labels / positions / patterns）。
 *
 * 原则：官方 BaskReport XML 一律先经 parseReport() 转成 ReportIR，再从 IR 提取，
 * 绝不直接正则解析 XML（真实 XML 中数据位是嵌套的 <dataset-content> / <text-content>，
 * 扁平正则假设（如 <content kind="dataset">）会静默匹配为空）。
 *
 * 提取流程（对每个报表）：
 *   1. 遍历 ir.cells，取 content.kind === 'dataset' 的 cell → (dataset, field, aggregate, row, col)
 *   2. label 匹配：同行左侧最近的非空 text cell（即真实 XML 的 <text-content>）；
 *      无则回退到 ir.datasets[].fields[].label（parseReport 已解析 <field label="...">）
 *   3. zone/side 推断：按行/列在网格中的比例分类（header / detail / footer，left / center / right）
 *   4. 输出 BindingKnowledge[]，由调用方聚合为 BindingDict
 */

import type { ReportIR } from '../ir/types.js';
import { parseReport } from '../xml/parse.js';

/** 单条绑定知识：报表中一个数据位学到的 (label, field, zone, side) 四元组。 */
export interface BindingKnowledge {
  label?: string;
  field: string;
  dataset?: string;
  aggregate?: string;
  zone?: string;
  side?: string;
  row?: number;
  col?: number;
  confidence: number;
  source?: string;
}

/** 知识库条目：同一 (label / position / pattern) 聚合出的候选字段列表。 */
export interface BindingDictEntry {
  fields: string[];
  confidence: number[];
  count: number;
  sources?: string[];
}

/** 绑定知识库：labels / positions / patterns 三层（结构与 bindingDict.ts 一致）。 */
export interface BindingDict {
  labels: Record<string, BindingDictEntry>;
  positions: Record<string, BindingDictEntry>;
  patterns: Record<string, BindingDictEntry>;
  version: number;
  miningDate: string;
  exampleCount: number;
  bindingCount: number;
}

/** 空知识库（无任何来源时）。 */
export function emptyBindingDict(): BindingDict {
  return {
    labels: {},
    positions: {},
    patterns: {},
    version: 0,
    miningDate: '',
    exampleCount: 0,
    bindingCount: 0,
  };
}

/**
 * 聚合多个报表/范例的绑定知识 → BindingDict。
 * 按 label / (zone_side) / (label@zone_side) 三层聚合，置信度取均值。
 */
export function aggregateBindings(allBindings: BindingKnowledge[]): BindingDict {
  const labelDict: Record<string, BindingDictEntry> = {};
  const positionDict: Record<string, BindingDictEntry> = {};
  const patternDict: Record<string, BindingDictEntry> = {};

  const push = (dict: Record<string, BindingDictEntry>, key: string, binding: BindingKnowledge) => {
    let entry = dict[key];
    if (!entry) {
      entry = { fields: [], confidence: [], count: 0, sources: [] };
      dict[key] = entry;
    }
    if (!entry.fields.includes(binding.field)) {
      entry.fields.push(binding.field);
      entry.confidence.push(binding.confidence);
    }
    entry.count += 1;
    if (binding.source && !entry.sources?.includes(binding.source)) {
      entry.sources?.push(binding.source);
    }
  };

  for (const binding of allBindings) {
    if (binding.label) push(labelDict, binding.label, binding);
    if (binding.zone && binding.side) {
      push(positionDict, `${binding.zone}_${binding.side}`, binding);
    }
    if (binding.label && binding.zone && binding.side) {
      push(patternDict, `${binding.label}@${binding.zone}_${binding.side}`, binding);
    }
  }

  // 置信度取均值（同一键下所有 field 共享平均置信度）
  const normalize = (entry: BindingDictEntry) => {
    const avg = entry.confidence.reduce((a, b) => a + b, 0) / entry.confidence.length;
    entry.confidence = entry.confidence.map(() => avg);
  };
  Object.values(labelDict).forEach(normalize);
  Object.values(positionDict).forEach(normalize);
  Object.values(patternDict).forEach(normalize);

  return {
    labels: labelDict,
    positions: positionDict,
    patterns: patternDict,
    version: 1,
    miningDate: new Date().toISOString(),
    exampleCount: new Set(allBindings.map((b) => b.source)).size,
    bindingCount: allBindings.length,
  };
}

/**
 * 从 ReportIR 提取绑定知识。
 *
 * @param ir     parseReport() 的产物（真实 XML 解析而来）
 * @param source 知识来源标识（如报表文件名），用于聚合时统计 exampleCount
 * @param maxRows 大模板只取前 N 行（控制提取范围）
 */
export function mineFromIr(ir: ReportIR, source?: string, maxRows: number = 100): BindingKnowledge[] {
  const bindings: BindingKnowledge[] = [];

  const cells = ir.cells ?? [];
  if (!cells.length) return bindings;

  const totalRows = Math.max(ir.rowCount ?? 1, ...cells.map((c) => c.row));
  const totalCols = Math.max(ir.columnCount ?? 1, ...cells.map((c) => c.col));

  // text cell 索引：(row,col) → 文本，用于同行左侧 label 匹配
  const textByPos = new Map<string, string>();
  for (const cell of cells) {
    if (cell.row > maxRows) continue;
    const content = cell.content;
    if (content?.kind === 'text' && content.value?.trim()) {
      textByPos.set(`${cell.row},${cell.col}`, content.value.trim());
    }
  }

  // 字段 label 回退表：dataset.field → label（来自 <field label="...">）
  const fieldLabelFallback = new Map<string, string>();
  for (const ds of ir.datasets ?? []) {
    for (const f of ds.fields ?? []) {
      if (f.name && f.label?.trim()) {
        fieldLabelFallback.set(`${ds.name}.${f.name}`, f.label.trim());
      }
    }
  }

  for (const cell of cells) {
    if (cell.row > maxRows) continue;
    const content = cell.content;
    if (!content || content.kind !== 'dataset') continue;
    if (!content.field) continue;

    // label：同行左侧最近的非空 text cell
    let label: string | undefined;
    for (let c = cell.col - 1; c >= 1; c--) {
      const t = textByPos.get(`${cell.row},${c}`);
      if (t) {
        label = t;
        break;
      }
    }
    // 回退：dataset 字段的官方 label
    if (!label) {
      label = fieldLabelFallback.get(`${content.dataset}.${content.field}`);
    }

    // zone：aggregate 语义优先，其次行比例
    //   - aggregate='group'（分组表头）→ header
    //   - 其他聚合（sum/avg/count…）→ footer（汇总行）
    //   - 否则按行比例（header / detail / footer）
    const normalizedRow = cell.row / totalRows;
    let zone: string;
    if (content.aggregate === 'group') zone = 'header';
    else if (content.aggregate && content.aggregate !== 'select') zone = 'footer';
    else if (normalizedRow < 0.05) zone = 'header';
    else if (normalizedRow > 0.9) zone = 'footer';
    else zone = 'detail';

    // side：列比例
    const normalizedCol = cell.col / totalCols;
    let side: string;
    if (normalizedCol < 0.33) side = 'left';
    else if (normalizedCol < 0.67) side = 'center';
    else side = 'right';

    bindings.push({
      label,
      field: content.field,
      dataset: content.dataset || undefined,
      aggregate: content.aggregate,
      zone,
      side,
      row: cell.row,
      col: cell.col,
      confidence: 0.95, // 报表 XML 中的绑定是高置信度真值
      source,
    });
  }

  return bindings;
}

/**
 * 从 BaskReport XML 提取绑定知识（parseReport → mineFromIr 一站式）。
 * 非报表（dashboard/解析失败）返回空数组，绝不抛错。
 */
export function extractBindingsFromReportXml(
  xmlContent: string,
  source?: string,
  maxRows: number = 100,
): BindingKnowledge[] {
  try {
    const ir = parseReport(xmlContent);
    if (!ir) return [];
    return mineFromIr(ir, source, maxRows);
  } catch {
    return [];
  }
}
